import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PLAN_CATALOG, fetchShelfIds } from '@/constants/plans';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_PAID_PLANS = PLAN_CATALOG.filter(p => p.id !== 'free' && p.price > 0);
const fmt = (v) => `R$ ${v.toFixed(2).replace('.', ',')}`;

export default function CadastroFeira() {
  const [searchParams] = useSearchParams();
  const campaign = searchParams.get('c') || 'feira';
  const resumeToken = searchParams.get('t');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ company_name: '', owner_name: '', whatsapp: '' });
  const [planId, setPlanId] = useState('enterprise');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [signup, setSignup] = useState(null);
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [alreadyActive, setAlreadyActive] = useState(false);
  const [assisted, setAssisted] = useState(null);
  const [promo, setPromo] = useState(null);
  const [shelfIds, setShelfIds] = useState(['enterprise', 'elite', 'five_stars']);
  const PAID_PLANS = ALL_PAID_PLANS.filter(p => shelfIds.includes(p.id));
  const promoCode = searchParams.get('promo');

  useEffect(() => { fetchShelfIds().then(setShelfIds); }, []);

  useEffect(() => {
    if (!promoCode) return;
    axios.get(`${API}/feira-promo/${promoCode}`).then(r => setPromo(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode]);

  useEffect(() => {
    if (!resumeToken) return;
    axios.get(`${API}/quick-signup/status`, { params: { t: resumeToken } })
      .then(r => {
        setSignup({ signup_token: resumeToken, company_name: r.data.company_name });
        setPlanId(r.data.plan_id);
        if (r.data.completed) setAlreadyActive(true);
        else setStep(2);
      })
      .catch(() => {});
  }, [resumeToken]);

  const clickTracked = useRef(false);
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && !clickTracked.current) {
      clickTracked.current = true;
      axios.post(`${API}/feira/click`, { ref }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plan = PAID_PLANS.find(p => p.id === planId);
  const freeDays = Math.max(30, promo?.first_due_days || 0);

  const submitStep1 = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/quick-signup`, { ...form, plan_id: planId, campaign, ref: searchParams.get('ref') || '', promo: promoCode || '', website: '' });
      setSignup(r.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cadastrar. Tente novamente.');
    } finally { setLoading(false); }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/quick-signup/complete`, { signup_token: signup.signup_token, cpf_cnpj: cpfCnpj });
      setDone(r.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar o boleto. Tente novamente.');
    } finally { setLoading(false); }
  };

  const activateAssisted = async () => {
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/quick-signup/assisted`, { signup_token: signup.signup_token, plan_id: planId });
      setAssisted(r.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao liberar o plano. Tente novamente.');
    } finally { setLoading(false); }
  };

  const inputCls = "w-full px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-slate-400 text-base focus:outline-none focus:ring-2 focus:ring-[#C5A059]";

  return (
    <div className="min-h-screen bg-[#0A2647] flex flex-col items-center justify-center p-5 py-10" data-testid="feira-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <p className="text-4xl mb-2">⭐⭐⭐⭐⭐</p>
          <h1 className="text-3xl font-bold text-[#C5A059]">Falo Com o Dono</h1>
          {step === 1 && <p className="text-slate-300 mt-2 text-base">Escute seus clientes antes do Google.<br />Ative sua empresa em <b className="text-white">1 minuto</b>.</p>}
          {step < 3 && (
            <div className="mt-3 inline-block bg-[#C5A059] text-[#0A2647] font-bold text-sm px-4 py-2 rounded-full" data-testid="feira-promo-badge">
              🎁 30 dias grátis: seu 1º boleto só vence em {freeDays} dias
            </div>
          )}
        </div>

        {alreadyActive && (
          <div className="bg-white/10 border border-[#C5A059]/40 rounded-2xl p-8 text-center" data-testid="feira-already-active">
            <p className="text-5xl mb-4">✅</p>
            <h2 className="text-xl font-bold text-white mb-2">{signup?.company_name} já está ativa!</h2>
            <p className="text-slate-300 text-sm">Seu plano já foi ativado. O acesso e o boleto estão no seu <b className="text-[#C5A059]">WhatsApp</b>.</p>
          </div>
        )}

        {step === 1 && !alreadyActive && (
          <form onSubmit={submitStep1} className="space-y-4">
            <div className="grid grid-cols-2 gap-2" data-testid="feira-plans">
              {PAID_PLANS.map(p => (
                <button key={p.id} type="button" data-testid={`feira-plan-${p.id}`}
                  onClick={() => setPlanId(p.id)}
                  className={`relative p-3 rounded-xl border text-center transition-colors ${planId === p.id ? 'bg-[#C5A059] text-[#0A2647] border-[#C5A059]' : 'bg-white/5 text-slate-300 border-white/15 hover:border-[#C5A059]/60'}`}>
                  {p.id === 'enterprise' && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-white text-[#0A2647] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow">RECOMENDADO</span>}
                  <p className="text-sm font-bold">{p.label}</p>
                  <p className="text-xs opacity-90">{fmt(p.price)}/mês</p>
                </button>
              ))}
            </div>
            <input required placeholder="Nome da sua empresa" value={form.company_name} data-testid="feira-company-input"
              onChange={e => setForm({ ...form, company_name: e.target.value })} className={inputCls} />
            <input required placeholder="Seu nome" value={form.owner_name} data-testid="feira-name-input"
              onChange={e => setForm({ ...form, owner_name: e.target.value })} className={inputCls} />
            <input required placeholder="Seu WhatsApp (DDD + número)" inputMode="tel" value={form.whatsapp} data-testid="feira-whatsapp-input"
              onChange={e => setForm({ ...form, whatsapp: e.target.value })} className={inputCls} />
            <input type="text" name="website" value="" onChange={() => {}} className="hidden" tabIndex={-1} autoComplete="off" />
            {error && <p className="text-red-400 text-sm text-center" data-testid="feira-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="feira-submit-btn"
              className="w-full bg-[#C5A059] text-[#0A2647] font-bold text-lg py-4 rounded-xl hover:bg-[#b8934d] transition-colors disabled:opacity-60">
              {loading ? 'Criando sua conta...' : `Começar ${freeDays} dias grátis →`}
            </button>
            <p className="text-slate-400 text-xs text-center" data-testid="feira-price-note">
              Depois {plan ? fmt(plan.price) : 'R$ 59,90'}/mês conforme o plano. Sem fidelidade.
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitStep2} className="space-y-4" data-testid="feira-step2">
            <div className="bg-white/10 border border-[#C5A059]/40 rounded-2xl p-5 text-center">
              <p className="text-3xl mb-2">🎉</p>
              <p className="text-white font-bold">{signup?.company_name} já está criada!</p>
              <p className="text-slate-300 text-sm mt-1">Seu acesso e sua placa com QR Code acabaram de chegar no seu <b className="text-[#C5A059]">WhatsApp</b>.</p>
            </div>
            <p className="text-slate-200 text-sm text-center">Último passo: seu <b className="text-[#C5A059]">CPF ou CNPJ</b> para gerar o boleto do plano <b>{plan?.label}</b> ({plan ? fmt(plan.price) : ''}/mês) — <b className="text-white">30 dias grátis</b>, o 1º só vence no mês que vem</p>
            <input required placeholder="CPF ou CNPJ" inputMode="numeric" value={cpfCnpj} data-testid="feira-cnpj-input"
              onChange={e => setCpfCnpj(e.target.value)} className={inputCls} />
            {error && <p className="text-red-400 text-sm text-center" data-testid="feira-error2">{error}</p>}
            <button type="submit" disabled={loading} data-testid="feira-complete-btn"
              className="w-full bg-[#C5A059] text-[#0A2647] font-bold text-lg py-4 rounded-xl hover:bg-[#b8934d] transition-colors disabled:opacity-60">
              {loading ? 'Gerando boleto...' : `Gerar meu boleto (vence em ${freeDays} dias) 📄`}
            </button>
            <p className="text-slate-400 text-xs text-center">Na página do boleto você também pode pagar com PIX ou cartão.</p>
            <div className="pt-2 border-t border-white/10">
              <button type="button" onClick={activateAssisted} disabled={loading} data-testid="feira-assisted-btn"
                className="w-full text-[#C5A059] text-sm font-semibold underline underline-offset-4 py-2 disabled:opacity-60">
                Não tenho o CNPJ agora — liberar o plano e enviar depois
              </button>
              <p className="text-slate-500 text-[11px] text-center mt-1">
                O plano {plan?.label} fica liberado na hora e o cliente recebe o link para informar o CNPJ no WhatsApp.
              </p>
            </div>
          </form>
        )}

        {step === 4 && assisted && (
          <div className="bg-white/10 border border-[#C5A059]/40 rounded-2xl p-8 text-center" data-testid="feira-assisted-success">
            <p className="text-5xl mb-4">🚀</p>
            <h2 className="text-xl font-bold text-white mb-2">Plano {assisted.plan_name} liberado!</h2>
            <p className="text-slate-300 text-sm leading-relaxed">
              {signup?.company_name} já pode usar tudo agora mesmo.<br />
              O link para informar o <b className="text-[#C5A059]">CPF/CNPJ</b> foi enviado no WhatsApp e será
              cobrado automaticamente até <b className="text-white">{assisted.deadline}</b> ({assisted.deadline_days} dias).
            </p>
          </div>
        )}

        {step === 3 && done && (
          <div className="bg-white/10 border border-[#C5A059]/40 rounded-2xl p-8 text-center" data-testid="feira-success">
            <p className="text-5xl mb-4">✅</p>
            <h2 className="text-xl font-bold text-white mb-2">Plano {done.plan_name} ativado!</h2>
            <p className="text-slate-300 text-sm leading-relaxed">
              {fmt(done.final_price || 0)}/mês · boleto vence em <b className="text-white">{done.due_date}</b>.<br />
              O boleto chegou no seu <b className="text-[#C5A059]">WhatsApp</b> — lá você também paga com PIX ou cartão.
            </p>
            {done.payment_url && (
              <a href={done.payment_url} target="_blank" rel="noreferrer" data-testid="feira-payment-link"
                className="inline-block mt-4 bg-[#C5A059] text-[#0A2647] font-bold px-6 py-3 rounded-xl">Ver boleto agora</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
